import React, { useEffect, useState } from "react";
import { Alert, Button, Group, Badge } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import DeviceInfoTable from "./DeviceInfoTable";
import { openConfirmation } from "@actions/actions";
import {
  connectADB,
  getSerial,
  populateDeviceInfo,
  disconnectADB,
  onAdbSessionLost,
  isAdbConnected,
} from "@utils/webusb-utils/ADBUtils";

const DeviceInfoWebUsb: React.FC<{
  setDevice;
  deviceInfoTableProps: {
    versionData;
    availableVersions;
    osUpdateRequired;
    appUpdateRequired;
    submitUpdate;
  };
}> = ({ setDevice, deviceInfoTableProps }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [connected, setConnected] = useState(false);
  const [working, setWorking] = useState(false);
  const [serial, setSerial] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [deviceInfo, setDeviceInfo] = useState<{
    volume: number | null;
    battery: number | null;
    puiVersion: string;
    version: string;
    hardwareModel: string;
    serialNo?: string;
    pid?: string;
  } | null>(null);

  // simple logger for ADB messages (can be extended to state/UI logs)
  const appendLog = (m: string) => {
    try {
      // keep console log and could be extended to UI
      console.log("adb:", m);
    } catch (e) {}
  };

  useEffect(() => {
    handleConnect();
  }, []);

  useEffect(
    () =>
      onAdbSessionLost(() => {
        setConnected(false);
        setDeviceInfo(null);
        setSerial(null);
        setDevice(null);
      }),
    [setDevice],
  );

  const handleConnect = async () => {
    setWorking(true);
    setConnectionError(null);
    try {
      await connectADB((m) => {
        appendLog(m);
      });
      setSerial(getSerial());
      try {
        const info = await populateDeviceInfo((m) => appendLog(m));
        setSerial(info.serialNo ?? null);
        setDeviceInfo(info as any);
        setDevice(info);
      } catch (e: any) {
        appendLog("Failed to populate device info: " + (e?.message || e));
        setDeviceInfo(null);
        setDevice(null);
      }
      // A transport death during populateDeviceInfo is swallowed above, and
      // races onAdbSessionLost's setConnected(false) — same guard as
      // AdbConnectionContext.tsx's connect().
      if (!isAdbConnected()) return;
      setConnected(true);
    } catch (e: any) {
      const msg = e?.message || String(e);
      setConnectionError(msg);
      setConnected(false);
      dispatch(
        openConfirmation(t("WebUsb.connectionFailedTitle"), msg, () => {}, true, t("close"), ""),
      );
    } finally {
      setWorking(false);
    }
  };

  const handleDisconnect = async () => {
    setWorking(true);
    try {
      await disconnectADB();
      setDeviceInfo(null);
      setSerial(null);
      setConnected(false);
      appendLog("Disconnected from device");
    } catch (e) {
      appendLog("Disconnect error: " + (e?.message || e));
    } finally {
      setWorking(false);
    }
  };

  // compose a minimal product object using serial as uuid
  const product = React.useMemo(
    () => ({
      // use deviceInfo when available
      uuid: deviceInfo?.pid || "",
      serialNo: deviceInfo?.serialNo || serial || "",
      pid: deviceInfo?.pid || "",
      puiVersion: deviceInfo?.puiVersion || "",
      version: deviceInfo?.version || "",
      hardwareModel: deviceInfo?.hardwareModel || "",
      battery: deviceInfo?.battery ?? null,
      volume: deviceInfo?.volume ?? null,
    }),
    [serial, deviceInfo],
  );

  const deviceStatusComponent = (
    <Group gap="sm">
      <Badge color={connected ? "green" : "red"} size="lg">
        {connected ? "Connected" : "Disconnected"}
      </Badge>
      {connected ? (
        <Button color="red" onClick={handleDisconnect} loading={working}>
          Disconnect
        </Button>
      ) : (
        <Button onClick={handleConnect} loading={working}>
          Connect
        </Button>
      )}
    </Group>
  );

  return (
    <>
      {connectionError ? (
        <Alert
          color="yellow"
          icon={<IconAlertTriangle size={16} />}
          title="USB connection failed"
          mb="sm"
          withCloseButton
          onClose={() => setConnectionError(null)}
        >
          {connectionError}
        </Alert>
      ) : null}
      <DeviceInfoTable
        {...deviceInfoTableProps}
        product={product}
        version={product?.version}
        deviceStatus={connected ? 1 : -1}
        deviceStatusComponent={deviceStatusComponent}
        canShowUpdateButton={true}
        hideUsbUpdateModal={true}
      />
    </>
  );
};

export default DeviceInfoWebUsb;
